import { useEffect, useState } from 'react';
import {
  countryName,
  type LeaderboardBoard,
  type LeaderboardEntry,
  type LeaderboardPage,
  type LeaderboardScope,
} from '@aiscoreboard/contracts';
import { CountryPicker, Flag } from '../components/CountryPicker';
import { fetchLeaderboard } from '../lib/accounts';
import { useT, type MessageKey } from '../lib/i18n';
import { pullToRefreshMessage, usePullToRefresh } from '../lib/usePullToRefresh';

interface Props {
  token: string | null;
  /** The viewer's club, used to default the city and country scopes. */
  home: { country: string; city: string | null } | null;
  onBack: () => void;
}

const BOARDS: [LeaderboardBoard, MessageKey, MessageKey][] = [
  ['rating', 'board.rating', 'board.ratingHint'],
  ['highest_break', 'board.highestBreak', 'board.highestBreakHint'],
  ['practice', 'board.practice', 'board.practiceHint'],
];

const SCOPES: [LeaderboardScope, MessageKey][] = [
  ['city', 'board.city'],
  ['country', 'board.country'],
  ['global', 'board.global'],
];

export function LeaderboardScreen({ token, home, onBack }: Props) {
  const t = useT();
  const [board, setBoard] = useState<LeaderboardBoard>('rating');
  const [scope, setScope] = useState<LeaderboardScope>(home?.city ? 'city' : 'global');
  const [country, setCountry] = useState(home?.country ?? '');
  const [city, setCity] = useState(home?.city ?? '');
  const [page, setPage] = useState<LeaderboardPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // A signed-out viewer has no home club, so the narrow scopes need the
  // place typed in before there is anything to ask the server for.
  const needsCountry = scope !== 'global' && country === '';
  const needsCity = scope === 'city' && city.trim() === '';
  const refresh = usePullToRefresh({
    onRefresh: async () => {
      if (needsCountry || needsCity) return;
      setLoading(true);
      setError(null);
      try {
        const next = await fetchLeaderboard(board, scope, token, {
          country: scope === 'global' ? undefined : country,
          city: scope === 'city' ? city : undefined,
        });
        setPage(next);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : t('board.failed'));
      } finally {
        setLoading(false);
      }
    },
  });
  const refreshMessage = pullToRefreshMessage(refresh.pullDistance, refresh.refreshing);

  useEffect(() => {
    if (needsCountry || needsCity) {
      setPage(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchLeaderboard(board, scope, token, {
      country: scope === 'global' ? undefined : country,
      city: scope === 'city' ? city : undefined,
    })
      .then((p) => {
        if (!cancelled) setPage(p);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('board.failed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [board, scope, country, city, token, needsCountry, needsCity, t]);

  const unit = board === 'practice' ? 'pts' : board === 'highest_break' ? '' : '';

  return (
    <main
      className="relative mx-auto flex h-full w-full max-w-2xl flex-col gap-3 overflow-y-auto p-4"
      onTouchStart={refresh.onTouchStart}
      onTouchMove={refresh.onTouchMove}
      onTouchEnd={refresh.onTouchEnd}
    >
      <p
        className="pointer-events-none absolute left-0 right-0 top-1 text-center text-xs text-white/50 transition-opacity"
        style={{ opacity: refresh.pullDistance > 0 || refresh.refreshing ? 1 : 0 }}
      >
        {refreshMessage === 'refreshing'
          ? t('common.loading')
          : refreshMessage === 'release'
            ? t('common.releaseToRefresh')
            : t('common.pullToRefresh')}
      </p>
      <header className="flex items-center gap-3">
        <button type="button" onClick={onBack} className="btn-ghost px-3 py-2 text-sm">
          ← {t('common.back')}
        </button>
        <h1 className="text-xl font-black">{t('board.title')}</h1>
      </header>

      <p className="panel shrink-0 text-sm text-white/70"><span className="block min-h-[2.5rem] max-sm:min-h-[5rem]">{t('board.liveOnly')}</span></p>

      <div className="flex shrink-0 gap-1 rounded-full bg-white/[0.06] p-1">
        {BOARDS.map(([key, label, hint]) => (
          <button
            key={key}
            type="button"
            title={t(hint)}
            onClick={() => setBoard(key)}
            className={[
              'min-w-0 flex-1 rounded-full px-2 py-2 text-sm font-semibold transition max-sm:min-h-[3.5rem]',
              board === key ? 'bg-white text-hud-bg' : 'text-white/70 hover:bg-white/10',
            ].join(' ')}
          >
            {t(label)}
          </button>
        ))}
      </div>

      <p className="min-h-[2.5rem] shrink-0 text-sm text-white/60">
        {t(board === 'rating' ? 'board.ratingHint' : board === 'highest_break' ? 'board.highestBreakHint' : 'board.practiceHint')}
      </p>

      <div className="flex gap-1 rounded-full bg-white/[0.06] p-1">
        {SCOPES.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setScope(key)}
            className={[
              'flex-1 rounded-full px-2 py-2 text-sm font-semibold transition',
              scope === key ? 'bg-ball-yellow text-hud-bg' : 'text-white/70 hover:bg-white/10',
            ].join(' ')}
          >
            {t(label)}
          </button>
        ))}
      </div>

      {scope !== 'global' && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="flex-1">
            <CountryPicker value={country} onChange={setCountry} label={t('board.country')} />
          </div>
          {scope === 'city' && (
            <div className="flex-1">
              <span className="label">{t('board.city')}</span>
              <input
                className="field"
                value={city}
                placeholder={t('board.clubCity')}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {(needsCountry || needsCity) && (
        <p className="panel text-sm text-white/60">
          {needsCountry ? t('board.needCountry') : t('board.needCity')}
        </p>
      )}

      {error && <p className="text-sm text-ball-red">{error}</p>}
      {loading && <p className="text-white/50">{t('common.loading')}</p>}

      {page && !loading && (
        <>
          {page.country && (
            <p className="flex items-center gap-2 text-sm text-white/50">
              <Flag code={page.country} />
              {page.city ? `${page.city}, ` : ''}
              {countryName(page.country)}
            </p>
          )}

          {page.entries.length === 0 ? (
            <p className="panel text-center text-white/50">{t('board.empty')}</p>
          ) : (
            <ol className="panel divide-y divide-white/10">
              {page.entries.map((e) => (
                <Row key={e.playerId} entry={e} unit={unit} me={page.viewer?.playerId === e.playerId} />
              ))}
            </ol>
          )}

          {page.viewer && !page.entries.some((e) => e.playerId === page.viewer?.playerId) && (
            <>
              <p className="text-center text-xs uppercase tracking-wider text-white/40">
                {t('board.yourPlace')}
              </p>
              <ol className="panel">
                <Row entry={page.viewer} unit={unit} me />
              </ol>
            </>
          )}
        </>
      )}
    </main>
  );
}

function Row({ entry, unit, me }: { entry: LeaderboardEntry; unit: string; me: boolean }) {
  return (
    <li
      className={[
        'flex items-center gap-3 py-2',
        me ? 'rounded-lg bg-ball-yellow/10 px-2' : '',
      ].join(' ')}
    >
      <span className="w-8 shrink-0 text-right num-lg text-white/50">
        {entry.rank}
      </span>
      <Flag code={entry.country} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">
          {entry.firstName} {entry.lastName}
        </p>
        <p className="truncate text-xs text-white/40">
          {[entry.clubName, entry.city].filter(Boolean).join(' · ') || entry.publicId}
        </p>
      </div>
      <span className="shrink-0 text-lg num-lg">
        {Math.round(entry.value * 100) / 100}
        {unit && <span className="ml-1 text-xs font-normal text-white/40">{unit}</span>}
      </span>
    </li>
  );
}
